import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHourglassStart,
    faLock,
    faCheckCircle,
    faUserSlash,
    faEllipsisH,
    faEye,
    faFileInvoice,
    faRotateLeft,
    faBan,
} from "@fortawesome/free-solid-svg-icons";
import PaymentMethodBadge from './PaymentMethodBadge';
import { faStar } from "@fortawesome/free-solid-svg-icons";

import { NavLink } from "react-router-dom";
import axios from "axios";

import DateCell from "../Listing/DateCell";
import fitColumns from "../Listing/fitColumns";
import { formatPrice } from "../../utils/FormatPrice";

import PatientImage from "../Patient/PatientImage";

import InputText from "../Form/Fields/InputText";
import InputSliderRange from "../Form/Fields/InputSliderRange";
import InputDateRange from "../Form/Fields/InputDateRange";
import InputPatient from "../Form/Fields/InputPatient";
import Select from "../Form/Fields/Select";
import Switcher from "../Form/Fields/Switcher";
import { getOrderStatusOptions } from "../../config/OrderStatus";
import { getPaymentMethodsOptions } from "../../config/PaymentMethod";

const getStatus = () => ({
    COMPLETE: { label: 'Terminé',    icon: faCheckCircle,    bg: "#dcfce7", color: "#16a34a", dot: "#16a34a" },
    PENDING:  { label: 'En attente', icon: faHourglassStart, bg: "#fffbeb", color: "#92400e", dot: "#f59e0b" },
    CANCELED: { label: 'Annulé',     icon: faLock,           bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" },
});

// Exported so the Orders listing's card view (Table's enableCardView, mobile) can reuse
// the exact same badge/amount rendering as the table columns below.
export const StatusBadge = ({ status }) => {
    const STATUS = getStatus();
    const s = STATUS[status] ?? STATUS.PENDING;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: s.bg, color: s.color,
            padding: "4px 10px", borderRadius: 999,
            fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
        }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
            {s.label}
        </span>
    );
};

export const formatAmount = (val) =>
    new Intl.NumberFormat("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

// Also used by the invoices listing (invoiceColumns.js)
export const TotalCell = ({ total, status }) => {
    const canceled = status === "CANCELED";
    return (
        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            {/* listing amount: 14px semi-bold, near-black, small gray "dhs" (same weight as the references) */}
            <span style={{
                fontSize: 14, fontWeight: 600,
                color: canceled ? "#d1d5db" : "#111827",
                textDecoration: canceled ? "line-through" : "none",
            }}>
                {formatAmount(total)}
            </span>
            <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>dhs</span>
        </div>
    );
};

const CollectedCell = ({ paid, total, status, t }) => {
    const canceled = status === "CANCELED";
    const fullyPaid = !canceled && paid >= total && total > 0;
    const ratio = total > 0 ? Math.min((paid / total) * 100, 100) : 0;
    const remaining = total - paid;

    const barColor = canceled ? "#e5e7eb" : fullyPaid ? "#16a34a" : ratio > 0 ? "#f59e0b" : "#e5e7eb";
    const amountColor = canceled ? "#d1d5db" : fullyPaid ? "#16a34a" : paid > 0 ? "#d97706" : "#111827";

    return (
        <div style={{ minWidth: 120 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: amountColor }}>
                    {formatAmount(paid)}
                </span>
                <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>dhs</span>
                {fullyPaid && (
                    <span style={{
                        fontSize: 10, fontWeight: 600, color: "#16a34a",
                        background: "#dcfce7", padding: "1px 6px", borderRadius: 99, marginLeft: 4,
                    }}>
                        {t("Paid")}
                    </span>
                )}
                {!fullyPaid && !canceled && remaining > 0 && (
                    <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>
                        -{formatAmount(remaining)}
                    </span>
                )}
            </div>
            <div style={{ height: 3, background: "#f3f4f6", borderRadius: 999, overflow: "hidden" }}>
                <div style={{
                    width: `${canceled ? 100 : ratio}%`,
                    height: "100%",
                    background: barColor,
                    borderRadius: 999,
                    transition: "width 0.4s ease",
                }} />
            </div>
        </div>
    );
};

// Default view: # · Vente · Total · Statut · Articles · Date · Patient · Paiement; Encaissé and
// Commentaires are hidden by default and can be shown from "Colonnes".
const buildOrderColumns = ({ openNewInvoiceModal, openCreateReturnModal, updateOrderStatus, onCancelOrder, onReload, canCancel = true, canCreateReturn = true, amountMax = 1000, t = (k) => k }) => [
    {
        Header: "#",
        accessor: "id",
        sortable: true,
        Cell: (row) => (
            <span style={{ fontSize: 14, fontWeight: 500, color: "#9ca3af" }}>{row.id}</span>
        ),
    },
    {
        Header: t("Order"),
        accessor: "reference",
        sortable: true,
        filterable: { component: InputText },
        Cell: (row) => (
            <div>
                {/* same look as the product name in the products listing (.cell-name), half a point
                    smaller: an all-caps/digits reference reads larger than a mixed-case name */}
                <NavLink to={`/order/${row.id}`} className="cell-name" style={{ whiteSpace: 'nowrap', fontSize: 13.5 }}>
                    {row.reference}
                </NavLink>
            </div>
        ),
    },
    {
        Header: t("Total"),
        accessor: "total",
        sortable: true,
        className: "text-end",
        filterable: { component: InputSliderRange, label: t("Total"), min: 0, max: amountMax, step: 1, unit: "dhs" },
        Cell: (row) => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <TotalCell total={row.total} status={row.status} />
            </div>
        ),
    },
    {
        Header: t("Status"),
        accessor: "status",
        filterable: { component: Select, options: getOrderStatusOptions(), label: "Status" },
        Cell: (row) => <StatusBadge status={row.status} />,
    },
    {
        Header: t("Items"),
        accessor: "itemCount",
        className: "text-center",
        Cell: (row) => (
            <span style={{ fontSize: 13.5, fontWeight: 500, color: "#374151", fontVariantNumeric: "tabular-nums" }}>
                {row.items?.length ?? 0}
            </span>
        ),
    },
    {
        // exact date + time, relative age underneath; sortable, and still the date-range filter
        Header: t("Date"),
        accessor: "createdAt",
        sortable: true,
        filterable: { component: InputDateRange, label: t("Order date") },
        Cell: (row) => <DateCell date={row.createdAt} />,
    },
    {
        Header: t("Patient"),
        accessor: "patientId",
        filterable: { component: InputPatient },
        Cell: (row) => {
            if (!row.patient) return (
                <div className="d-flex align-items-center gap-2">
                    <span style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <FontAwesomeIcon icon={faUserSlash} style={{ color: "#9ca3af", fontSize: 13 }} />
                    </span>
                    <span className="cell-sub" style={{ marginTop: 0 }}>{t("Visitor")}</span>
                </div>
            );

            return (
                <div className="d-flex align-items-center gap-2">
                    <PatientImage patient={row.patient} size={32} />
                    <div>
                        <NavLink className="cell-name" style={{ fontSize: 13 }} to={`/patient/${row.patient.id}`}>
                            {row.patient.firstName}{row.patient.lastName ? ` ${row.patient.lastName[0]}.` : ''}
                        </NavLink>
                    </div>
                </div>
            );
        },
    },
    {
        Header: t("Paiement"),
        accessor: "paymentMethods",
        filterable: {
            component: Select,
            label: 'Moyen de paiement',
            filterName: 'paymentMethod',
            options: [{ value: '', label: 'Tous' }, ...getPaymentMethodsOptions()],
        },
        Cell: (row) => {
            const methods = row.paymentMethods;
            const hasLoyalty = row.loyaltyPointsUsed > 0;
            // A points-only order still gets a payment record (method defaults to CASH) so it has
            // an invoice — but 0 dhs was actually collected that way, so it shouldn't render as a
            // real "Espèces" badge next to "Points fidélité".
            const hasPayments = methods && methods.length > 0 && row.paiedAmount > 0;
            if (!hasPayments && !hasLoyalty) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
            // Only the first invoice's payment method — a multi-invoice order (split payments,
            // an extra installment...) shouldn't stack one badge per distinct method used.
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {hasPayments && <PaymentMethodBadge method={methods[0]} />}
                    {hasLoyalty && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: '#fef3c7', color: '#b45309',
                            padding: '3px 9px', borderRadius: 999,
                            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                        }}>
                            <FontAwesomeIcon icon={faStar} style={{ fontSize: 10 }} />
                            Points fidélité
                        </span>
                    )}
                </div>
            );
        },
    },
    {
        Header: t("Collected"),
        accessor: "paiedAmount",
        visible: false, // secondary: in "Colonnes" (the status already says paid / pending)
        sortable: true,
        // filterName: the backend's paid-amount filter is totalPaidMin/Max (OrderFilterConfig)
        filterable: { component: InputSliderRange, label: t("Collected"), filterName: "totalPaid", min: 0, max: amountMax, step: 1, unit: "dhs" },
        Cell: (row) => <CollectedCell paid={row.paiedAmount} total={row.total} status={row.status} t={t} />,
    },
    {
        Header: "Commentaires",
        accessor: "hasNotes",
        visible: false,
        filterable: { component: Switcher, label: "Avec commentaires", size: "mini", showMiniLabel: true, onLabel: "Oui", offLabel: "Non" },
    },
    {
        Header: "",
        accessor: "actions",
        className: "col-actions",
        Cell: (row) => (
            <div className="cell-dropdown">
                <button
                    className="cell-actions-btn dropdown-toggle"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                >
                    <FontAwesomeIcon icon={faEllipsisH} />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                        <NavLink to={`/order/${row.id}`} className="dropdown-item">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            {t("View order")}
                        </NavLink>
                    </li>
                    {row.status === "PENDING" && (
                        <li>
                            <button className="dropdown-item" onClick={() => openNewInvoiceModal(row)}>
                                <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                                {t("New invoice")}
                            </button>
                        </li>
                    )}
                    {(canCreateReturn || canCancel) && row.status !== "CANCELED" && (
                        <li><hr className="dropdown-divider" /></li>
                    )}
                    {canCreateReturn && row.status !== "CANCELED" && (
                        <li>
                            <button className="dropdown-item" onClick={() => openCreateReturnModal?.(row)}>
                                <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                                {t("Create return")}
                            </button>
                        </li>
                    )}
                    {canCancel && row.status !== "CANCELED" && (
                        <li>
                            <button
                                className="dropdown-item danger"
                                onClick={() => onCancelOrder ? onCancelOrder(row.id) : updateOrderStatus(row.id, "CANCELED")}
                            >
                                <FontAwesomeIcon icon={faBan} className="me-2" />
                                {t("Cancel order")}
                            </button>
                        </li>
                    )}
                </ul>
            </div>
        ),
    },
];

// only the reference ("Vente") stretches, the others fit their content (fitColumns)
export const getOrderColumns = (options) => fitColumns(buildOrderColumns(options), 'reference');
